import { fromJSON } from '@/common/helpers/object/object';
import { RoundSpinner } from '@/shared/ui/components';
import { gql, useQuery } from '@apollo/client';
import * as React from 'react';
import { BASIC_PROFILE } from '~/src/redesign/user/graphql/basic-profile.query';
import { useGlobalState } from '~/common/hooks/useGlobalState';
import { TokenPage } from '~/src/modules/auth/token/Token.styled';
import { tokenIsValid } from '~/src/shared/utils/user';

interface Configs {
  SHOW_BID_RANGE: boolean;
  SHIFT_PREFERENCES_ENABLE: boolean;
  RECOMMENDED_SHIFTS: boolean;
  SHIFT_REVIEW_ENABLE: boolean;
  SHIFT_CANCELLATION_ENABLE: boolean;
  SHIFT_CANCELLATION_WINDOW_INITIAL: number;
  SHIFT_CANCELLATION_WINDOW_FINAL: number;
  SHIFT_INFO_VISIBILITY_ENABLE: boolean;
  SHIFT_INFO_VISIBILITY_THRESHOLD: number;
  ENABLE_BUY_IT_NOW: boolean;
  HOSPITAL_PROFILE_ENABLE: boolean;
  ENABLE_DYNAMIC_HOSPITAL_SHIFT_TYPE: boolean;
  ENABLE_ONBOARDING_RECOVERY: boolean;
  ENABLE_HALF_DAY_BIDDING: boolean;
  PETCO_CAREERS_URL: string;
  ONBOARDING_TA_TEAM_CONTACT: string;
  GA_CLIENT_STREAM: string;
  ENABLE_NEW_DVM_APP: boolean;
  TERMS_PUBLISH_DATE: string;
  SCHEDULE_EMAIL_ADDRESS: string;
}

// @ts-expect-error: Failing at runtime is actually better if this ever happens
export const ConfigContext = React.createContext<Configs>();

export function ConfigProvider(props: React.PropsWithChildren) {
  const globalState = useGlobalState();
  const profile = useQuery(BASIC_PROFILE, {
    skip: !tokenIsValid(globalState.state.userToken),
  });

  const { data, loading } = useQuery<{
    configs: { name: string; value: string }[];
  }>(gql`
    query dynamicSettings {
      configs {
        name
        value
      }
    }
  `);

  const configs = React.useMemo(() => {
    try {
      const configs: Partial<Configs> = {};
      data?.configs?.forEach(({ name, value }) => {
        configs[name as keyof Configs] = fromJSON(value, value);
      });
      return configs;
    } catch (error) {
      console.error(error);
      return {};
    }
  }, [data]);

  if (loading || profile.loading) {
    return (
      <TokenPage>
        <RoundSpinner />
      </TokenPage>
    );
  }

  return <ConfigContext.Provider value={configs as Configs} {...props} />;
}

export const useConfigs = () => React.useContext(ConfigContext);
